import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Link, NavLink, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Badge, Layout, Menu } from 'antd';
import clsx from 'clsx';
import _isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';

import { FEATURES, IS_PRODUCTION, TENANTS } from 'appConstants';
import { FeatureFlag } from 'components/FeatureFlag';
import MenuIcon from 'components/Header/MenuIcon';
import Ability from 'containers/Ability';
import Analytics from 'containers/Analytics';
import {
  getCampaignPendingRequest,
  getCustomerPendingRequest,
  getNecessities,
  getRewardPendingRequest,
  getRulePendingRequest
} from 'containers/App/actions';
import IdleHandler from 'containers/App/components/IdleHandler';
import UserMenu from 'containers/App/components/UserMenu';
import { Logo, Sider } from 'containers/App/style';
import Audience from 'containers/Audience';
import PointsRedemptions from 'containers/BDO/pointsRedemptions';
import CreditCardRedemptionSettings from 'containers/BDO/settings';
import { useBDOLoyaltyEnabled } from 'containers/BDO/utils';
import { getLoyaltyPrograms } from 'containers/Loyalty/actions';
import { getPermissions } from 'containers/Permissions/actions';
import { useAuthUser, useFeatureFlags, useTenant, useTypedSelector } from 'hooks';
import ForbiddenPage from 'pages/public/AccessDenied';
import { DashboardLoading, PageLoading } from 'pages/public/Loading';
import NotFoundPage from 'pages/public/NotFoundPage';
import { lazyWithRetry } from 'utils/lazyWithRetry';

import ReloadMessage from '../components/ReloadMessage';
import { getFilteredMenu } from '../getMenu';
import { usePendo } from '../usePendo';
import POSableLogo from './POSable_logo.png';
import AffirmLogo from './affirm_logo.png';
import { ReactComponent as PerxLogo } from './ic_logo.svg';
import './index.less';
import ErrorFallback from '../components/ErrorFallback';
import PoweredByPerx from './powered_by_perx.png';
import RushLogo from './rush_logo.png';
import ZealLogo from './zeal_logo.png';
import { PerxState } from 'MyTypes';

const Settings = lazyWithRetry(() => import('containers/Setting'));
const Leaderboards = lazyWithRetry(() => import('containers/Leaderboards'));
const Marketplace = lazyWithRetry(() => import('containers/Marketplace/pages/Marketplace'));
const Report = lazyWithRetry(() => import('containers/Report'));
const Reward = lazyWithRetry(() => import('containers/Reward'));
const Catalogue = lazyWithRetry(() => import('containers/Catalogue'));
const Merchant = lazyWithRetry(() => import('containers/Merchant'));
const Campaign = lazyWithRetry(() => import('containers/Campaign'));
const Loyalty = lazyWithRetry(() => import('containers/Loyalty'));
const Customer = lazyWithRetry(() => import('containers/Customer'));
const BulkAction = lazyWithRetry(() => import('containers/BulkAction'));
const Rules = lazyWithRetry(() => import('containers/Rule'));
const Inventory = lazyWithRetry(() => import('containers/Inventory'));
const Partners = lazyWithRetry(() => import('containers/Partners'));
const Badges = lazyWithRetry(() => import('containers/Badge'));
const UserProfile = lazyWithRetry(() => import('containers/UserProfile'));
const Microsite = lazyWithRetry(() => import('containers/Microsite'));
const QuickSight = lazyWithRetry(() => import('containers/Analytics/QuickSight'));

const Forbidden = () => <ForbiddenPage />;

const ProtectedReward = props => (
  <Ability renderNoAccess={Forbidden} access="rewards">
    <Reward {...props} />
  </Ability>
);

const ProtectedMarketplace = props => (
  <Ability renderNoAccess={Forbidden} access="rewards">
    <Marketplace {...props} />
  </Ability>
);

const ProtectedCatalog = props => (
  <Ability renderNoAccess={Forbidden} access="catalogs">
    <Catalogue {...props} />
  </Ability>
);

// v6 helpers: routes that consume URL params + search params before
// redirecting (previously expressed as `<Route render={({match}) => ...}/>`).
const RewardRequestCommentsRedirect = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const rewardCampaignId = new URLSearchParams(location.search).get('reward_campaign_id');
  return <Navigate to={`/p/rewards/show/${rewardCampaignId}/reward_request/${id}`} replace />;
};

const CampaignRequestCommentsRedirect = () => {
  const { id } = useParams<{ id: string }>();
  const loc = useLocation();
  const campaignId = new URLSearchParams(loc.search).get('campaign_id');
  return <Navigate to={`/p/campaigns/show/${campaignId}/campaign_request/${id}`} replace />;
};

const RuleRequestCommentsRedirect = () => {
  const { id } = useParams<{ id: string }>();
  const loc = useLocation();
  const transactionRuleId = new URLSearchParams(loc.search).get('transaction_rule_id');
  return <Navigate to={`/p/rules/show/${transactionRuleId}/rule_request/${id}`} replace />;
};

const ProtectedCampaign = props => (
  <Ability renderNoAccess={Forbidden} access="campaigns">
    <Campaign {...props} />
  </Ability>
);

const ProtectedMerchant = props => (
  <Ability renderNoAccess={Forbidden} access="merchants">
    <Merchant {...props} />
  </Ability>
);

const ProtectedLoyalty = props => (
  <Ability renderNoAccess={Forbidden} access="loyalties">
    <Loyalty {...props} />
  </Ability>
);

const ProtectedPartner = props => (
  <Ability renderNoAccess={Forbidden} access="partners">
    <Partners {...props} />
  </Ability>
);

const ProtectedSettings = props => (
  <Ability renderNoAccess={Forbidden} access="settings">
    <Settings {...props} />
  </Ability>
);

const ProtectedInventory = props => (
  <Ability renderNoAccess={Forbidden} access="inventories">
    <Inventory {...props} />
  </Ability>
);

const IndexPage = () => {
  usePendo();
  const authUser = useAuthUser();
  const roles = authUser?.roles || [];
  const currentUserID = authUser?.id;

  const isAdmin = roles.some(role => ['tenant admin', 'Tenant Admin'].includes(role.name));

  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const permissions = useTypedSelector(state => state.permissions.permissions, shallowEqual);
  const { name: tenantName, features } = useTenant();
  const [collapsed, setCollapsed] = useState(true);
  const { currentTheme } = useThemeSwitcher();
  const makerCheckerCustomerManagement = features[FEATURES.MAKER_CHECKER_CUSTOMER_MANAGEMENT.key];
  const makerCheckerReward = features[FEATURES.MAKER_CHECKER_REWARD.key];
  const makerCheckerRule = features[FEATURES.MAKER_CHECKER_RULE.key];
  const makerCheckerCampaign = features[FEATURES.MAKER_CHECKER_CAMPAIGN.key];

  const customerPendingRequest = useSelector(
    (state: PerxState) => state.global.customerPendingRequest
  );

  const campaignPendingRequest = useSelector(
    (state: PerxState) => state.global.campaignPendingRequest
  );
  const rewardPendingRequest = useSelector((state: PerxState) => state.global.rewardPendingRequest);
  const rulePendingRequest = useSelector((state: PerxState) => state.global.rulePendingRequest);

  useEffect(() => {
    if (makerCheckerCustomerManagement) {
      dispatch(getCustomerPendingRequest({ params: { status: 'pending' } }));
    }
    if (makerCheckerCampaign) {
      dispatch(getCampaignPendingRequest({ params: { status: 'pending' } }));
    }
    if (makerCheckerReward) {
      dispatch(getRewardPendingRequest({ params: { status: 'pending' } }));
    }
    if (makerCheckerRule) {
      dispatch(getRulePendingRequest({ params: { status: 'pending' } }));
    }
    // Fetch pending counts once on mount. The error?.status deps were removed
    // because the REQUEST reducer clears error (→ undefined) while the FAIL
    // reducer sets it back (→ 403/500), creating an oscillating dep that
    // caused an infinite dispatch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    makerCheckerCustomerManagement,
    makerCheckerCampaign,
    makerCheckerReward,
    makerCheckerRule
  ]);

  const {
    audience: showAudience,
    catalogs: showCatalogs,
    inventories: showInventory,
    reward_budgeting: showPartners,
    rewards_marketplace: showMarketplace,
    leaderboards: showLeaderboards,
    badges: showBadges,
    loyalties: showLoyalties,
    microsite: showMicrosite,
    merchants: showMerchants,
    quicksight: showQuickSight
  } = useFeatureFlags();

  const [selectedMenu, setSelectedMenu] = useState(
    _isEmpty(permissions) ? '' : Object.keys(permissions)[0]
  );

  const onSidebarToggle = () => setCollapsed(prev => !prev);

  const onMenuClick = evt => setSelectedMenu(evt.key);

  useEffect(() => {
    // get audience list
    // get tags
    // get labaels
    // get categories
    dispatch(getNecessities());
    dispatch(getLoyaltyPrograms());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getPermissions());
    // eslint-disable-next-line
  }, [tenantName]);

  const bdoLoyaltyEnabled = useBDOLoyaltyEnabled();

  const menu = useMemo(() => {
    // every time permission changes, get the filtered menu items from MENU
    const modifiedPermissions = {
      ...permissions,
      quicksight: true,
      marketplace: showMarketplace
    };
    let newMenu = getFilteredMenu(modifiedPermissions);

    if (!showAudience) {
      newMenu = newMenu.filter(x => x.key !== 'audience');
    }

    if (!showCatalogs) {
      newMenu = newMenu.filter(x => x.key !== 'catalogs');
    }

    if (!showInventory) {
      newMenu = newMenu.filter(x => x.key !== 'inventories');
    }

    if (!showMarketplace) {
      newMenu = newMenu.filter(x => x.key !== 'marketplace');
    }

    if (!showPartners) {
      newMenu = newMenu.filter(x => x.key !== 'partners');
    }

    if (!showLeaderboards) {
      newMenu = newMenu.filter(x => x.key !== 'leaderboards');
    }

    if (!showBadges) {
      newMenu = newMenu.filter(x => x.key !== 'badges');
    }

    if (!showLoyalties) {
      newMenu = newMenu.filter(x => x.key !== 'loyalties');
    }

    if (!showMicrosite) {
      newMenu = newMenu.filter(x => x.key !== 'microsites');
    }

    if (!showMerchants) {
      newMenu = newMenu.filter(x => x.key !== 'merchants');
    }

    if (!bdoLoyaltyEnabled) {
      newMenu = newMenu.filter(x => x.key !== 'points_redemptions');
    }

    if (!isAdmin) {
      newMenu = newMenu.filter(x => x.key !== 'quicksight');
    }

    if (!showQuickSight) {
      newMenu = newMenu.filter(x => x.key !== 'quicksight');
    }

    return newMenu;
    // eslint-disable-next-line
  }, [bdoLoyaltyEnabled, permissions, tenantName, features]);

  useEffect(() => {
    const activeMenuItem = menu.find(element => location.pathname.includes(element.url));
    if (activeMenuItem && activeMenuItem.key) {
      setSelectedMenu(_ => activeMenuItem.key);
    }
  }, [location.pathname, menu]);

  const showPoweredByPerx = tenantName === TENANTS.CWC;

  if (!tenantName) {
    return <DashboardLoading />;
  }

  return (
    <Layout>
      <IdleHandler />
      <ScrollableSider
        breakpoint="lg"
        collapsible
        collapsed={collapsed}
        onCollapse={onSidebarToggle}
        style={{
          height: '100vh',
          position: 'sticky',
          top: 0,
          left: 0,
          zIndex: 20, // need this to put sider on top of forms\' bottom bar, TODO: reevaluate after looking into BottomBar
          display: 'flex',
          flexDirection: 'column'
        }}
        className={clsx(currentTheme)}
      >
        <Logo>
          <Link to={'/'}>
            <AppLogo />
          </Link>
        </Logo>
        <UserMenu />
        <Menu
          theme="dark"
          mode="inline"
          style={{ borderRight: 'none' }}
          onClick={onMenuClick}
          selectedKeys={[selectedMenu]}
          items={menu?.map(item => ({
            key: item.key,
            label: (
              <NavLink to={item.url}>
                {item.description}
                {makerCheckerCustomerManagement &&
                item.description === 'Customers' &&
                customerPendingRequest?.meta?.count > 0 ? (
                  <Badge status="processing" style={{ marginLeft: '5px' }} />
                ) : null}

                {makerCheckerReward &&
                item.description === 'Rewards' &&
                rewardPendingRequest?.meta?.count > 0 ? (
                  <Badge status="processing" style={{ marginLeft: '5px' }} />
                ) : null}

                {makerCheckerRule &&
                item.description === 'Rules' &&
                rulePendingRequest?.meta?.count > 0 ? (
                  <Badge status="processing" style={{ marginLeft: '5px' }} />
                ) : null}

                {makerCheckerCampaign &&
                  item.key === 'campaigns' &&
                  campaignPendingRequest?.meta?.count > 0 && (
                    <Badge status="processing" style={{ marginLeft: '5px' }} />
                  )}
              </NavLink>
            ),
            icon: <MenuIcon src={item.svg} className="menu-icon" />
          }))}
        />
        {showPoweredByPerx && <PoweredBy alt="Powered by Perx" src={PoweredByPerx} />}
      </ScrollableSider>
      <Layout
        style={{
          //overflow should be unset to allow position sticky items to work in content
          overflowX: 'unset',
          //min-width is set to auto for flex items cause it to expand based on content. This performs same function as overflow-x hidden
          minWidth: 0
        }}
      >
        <Layout.Content>
          <Sentry.ErrorBoundary
            fallback={({ error, eventId }) =>
              error.name === 'ChunkLoadError' ? (
                <ReloadMessage />
              ) : (
                <ErrorFallback eventId={eventId} />
              )
            }
          >
            <Suspense fallback={<PageLoading />}>
              <Routes>
                {!_isEmpty(permissions) && menu[0] && (
                  <Route path="" element={<Navigate to={menu[0].url} replace />} />
                )}
                <Route path="reports/*" element={<Report />} />
                <Route
                  path="marketplace/*"
                  element={showMarketplace ? <ProtectedMarketplace /> : null}
                />
                <Route path="rewards/*" element={<ProtectedReward />} />
                <Route
                  path="reward_requests"
                  element={<Navigate to="/p/rewards" state={{ tab: 'pending' }} replace />}
                />
                <Route
                  path="reward_requests/:id/comments"
                  element={<RewardRequestCommentsRedirect />}
                />
                <Route path="catalogues/*" element={<ProtectedCatalog />} />
                {showMerchants && <Route path="merchants/*" element={<ProtectedMerchant />} />}
                <Route path="campaigns/*" element={<ProtectedCampaign />} />
                <Route
                  path="campaign_requests"
                  element={<Navigate to="/p/campaigns" state={{ tab: 'pending' }} replace />}
                />
                <Route
                  path="campaign_requests/:id/comments"
                  element={<CampaignRequestCommentsRedirect />}
                />
                {/*<Route path="badges/*" element={<ProtectedBadge />} />*/}
                {showBadges && <Route path="badges/*" element={<Badges />} />}
                <Route
                  path="leaderboards/*"
                  element={
                    <FeatureFlag flags={[FEATURES.LEADERBOARDS]}>
                      <Leaderboards />
                    </FeatureFlag>
                  }
                />
                {showLoyalties && <Route path="loyalty/*" element={<ProtectedLoyalty />} />}
                <Route path="settings/*" element={<ProtectedSettings />} />
                <Route path="customers/*" element={<Customer />} />
                <Route path="bulk_actions/*" element={<BulkAction />} />
                <Route path="business_intelligence/*" element={<Analytics />} />
                {isAdmin && showQuickSight && (
                  <Route path="quicksight/*" element={<QuickSight />} />
                )}
                <Route path="rules/*" element={<Rules />} />
                <Route
                  path="transaction_rules"
                  element={<Navigate to="/p/rules" state={{ tab: 'pending' }} replace />}
                />
                <Route
                  path="rule_requests/:id/comments"
                  element={<RuleRequestCommentsRedirect />}
                />
                <Route path="inventories/*" element={<ProtectedInventory />} />
                <Route path="partners/*" element={showPartners ? <ProtectedPartner /> : null} />
                <Route path="microsite/*" element={showMicrosite ? <Microsite /> : null} />
                <Route path="audiences/*" element={<Audience />} />
                <Route path="user-profile/*" element={<UserProfile />} />
                <Route path="points_redemptions/*" element={<PointsRedemptions />} />
                <Route
                  path="credit_card_redemption_settings/*"
                  element={<CreditCardRedemptionSettings />}
                />
                <Route element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </Sentry.ErrorBoundary>
        </Layout.Content>
      </Layout>
    </Layout>
  );
};

const ScrollableSider = styled(Sider)`
  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;

    .ant-menu {
      flex: 1;
      overflow-y: auto;
      scrollbar-width: none;

      ::-webkit-scrollbar {
        display: none;
      }
    }
  }
`;
const PoweredBy = styled.img`
  margin: 0 auto;
  width: 64px;
`;

export default IndexPage;

const AppLogo = () => {
  const { name: tenantName } = useTenant();
  let logo;

  switch (tenantName) {
    case TENANTS.ZEAL:
      logo = IS_PRODUCTION ? (
        <LogoImage src={ZealLogo} alt="Logo" width={'50'} />
      ) : (
        <LogoImage src={POSableLogo} alt="Logo" width={'50'} />
      );
      break;
    case TENANTS.RUSH:
    case TENANTS.CWC:
      logo = <LogoImage src={RushLogo} alt="Logo" style={{ width: 120 }} />;
      break;
    case TENANTS.AFFIRM:
      logo = <LogoImage src={AffirmLogo} alt={'Logo'} width={'85'} />;
      break;
    default:
      logo = <PerxLogo width={'50px'} />;
      break;
  }

  return logo;
};

const LogoImage = styled.img`
  max-width: 100%;
`;
